<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import VIcon from "../Icon/VIcon.vue";
import VTooltip from "../Tooltip/VTooltip.vue";

interface Props {
  steps: string[] | number
  /**
   * `labeled` shows the name beside each badge. When the names do not fit, only the current step keeps its
   * name (truncated if even that does not fit) and the others show their badge.
   * `compact` is badges only, for a dialog footer between Cancel and Next.
   * `current` is the App Store rail: 24px badges joined by short 2px lines, and only the current step shows its
   * name. Used at the bottom of a wizard dialog between Previous and Next, and on top of the consent screen.
   * A step whose name is not fully visible shows it in a tooltip on hover and focus; the name is always the
   * accessible label.
   */
  variant?: "labeled" | "compact" | "current"
  /**
   * Only for `current`: the lines between the steps grow to fill the width (consent screen) instead of the short
   * lines with a line before the first and after the last step (dialog footer).
   */
  stretch?: boolean
  /** Completed steps are clickable so the user can go back. Forward stays blocked. */
  interactive?: boolean
  valueText?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: "labeled",
  interactive: true,
  valueText: undefined,
  stretch: false,
});

const currentStep = defineModel<number>("modelValue", { default: 0 });

const labels = computed<string[]>(() => {
  if (typeof props.steps === "number") {
    return Array.from<string>({ length: Math.max(0, props.steps) }).fill("");
  }

  return props.steps;
});

const isCurrentVariant = computed(() => props.variant === "current");
const showLabels = computed(() => props.variant === "labeled" || isCurrentVariant.value);

// Labels truncate when the rail is squeezed; once any of them would be cut off, only the current one stays.
// The width the full labels needed is kept, so they come back as soon as the rail is that wide again.
const root = useTemplateRef<HTMLElement>("root");
const labelsCollapsed = ref(false);
const truncatedSteps = ref(new Set<number>());
let expandedWidth = 0;

function measureTruncation() {
  const truncated = new Set<number>();
  for (const label of root.value?.querySelectorAll<HTMLElement>("[data-step-label]") ?? []) {
    if (label.scrollWidth > label.clientWidth) {
      truncated.add(Number(label.dataset.stepLabel));
    }
  }
  truncatedSteps.value = truncated;
}

function fitLabels() {
  const el = root.value;
  if (!el || !showLabels.value || el.clientWidth === 0) {
    return;
  }

  if (labelsCollapsed.value) {
    if (el.clientWidth >= expandedWidth) {
      labelsCollapsed.value = false;
      void nextTick(fitLabels);
      return;
    }
    measureTruncation();
    return;
  }

  let overflow = 0;
  for (const label of el.querySelectorAll<HTMLElement>("[data-step-label]")) {
    overflow += Math.max(0, label.scrollWidth - label.clientWidth);
  }
  if (overflow > 0) {
    expandedWidth = el.clientWidth + overflow;
    labelsCollapsed.value = true;
    void nextTick(measureTruncation);
    return;
  }
  truncatedSteps.value = new Set();
}

useResizeObserver(root, fitLabels);
watch([labels, currentStep], () => {
  void nextTick(fitLabels);
});
watch(labels, () => {
  labelsCollapsed.value = false;
  expandedWidth = 0;
});

function showsLabel(index: number, label: string) {
  if (isCurrentVariant.value) {
    return Boolean(label) && isCurrent(index);
  }
  return showLabels.value && Boolean(label) && (!labelsCollapsed.value || isCurrent(index));
}

function needsTooltip(index: number, label: string) {
  return Boolean(label) && (!showsLabel(index, label) || truncatedSteps.value.has(index));
}

const valueNow = computed(() => currentStep.value + 1);

const resolvedValueText = computed(() => {
  if (props.valueText) {
    return props.valueText;
  }

  const label = labels.value[currentStep.value];
  const position = `${valueNow.value} / ${labels.value.length}`;

  return label ? `${position} · ${label}` : position;
});

function isCompleted(index: number) {
  return index < currentStep.value;
}

function isCurrent(index: number) {
  return index === currentStep.value;
}

function canGoTo(index: number) {
  return props.interactive && isCompleted(index);
}

function goToStep(index: number) {
  if (!canGoTo(index)) {
    return;
  }

  currentStep.value = index;
}

function badgeClass(index: number) {
  if (isCurrentVariant.value) {
    return currentVariantBadgeClass(index);
  }

  if (isCurrent(index)) {
    return "bg-primary-500 text-white";
  }

  if (isCompleted(index)) {
    return "bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300";
  }

  return "bg-gray-100 text-gray-400 dark:bg-dark-700 dark:text-gray-500";
}

// Board rail: current #4CAF50 with a white number, done #DCEEDC with a green check, to come #F0F2F0 with a grey number.
function currentVariantBadgeClass(index: number) {
  if (isCurrent(index)) {
    return "bg-primary-600 text-white";
  }

  if (isCompleted(index)) {
    return "bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300";
  }

  return "bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-400";
}

/** Line after step `index` (or before the first step for -1): green up to the current step. */
function currentVariantLineClass(index: number) {
  return index < currentStep.value ? "bg-primary-600 dark:bg-primary-500" : "bg-gray-200 dark:bg-dark-600";
}

function labelClass(index: number) {
  if (isCurrentVariant.value) {
    return "font-medium text-(--text)";
  }

  if (isCurrent(index)) {
    return "font-semibold text-gray-800 dark:text-white";
  }

  if (isCompleted(index)) {
    return "text-gray-600 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400";
  }

  return "text-gray-400 dark:text-gray-500";
}
</script>

<template>
  <div
    v-if="labels.length && isCurrentVariant"
    ref="root"
    class="flex min-w-0 items-center"
    :class="props.stretch ? 'w-full' : 'justify-center'"
    role="progressbar"
    :aria-valuemin="1"
    :aria-valuemax="labels.length"
    :aria-valuenow="valueNow"
    :aria-valuetext="resolvedValueText"
  >
    <ol class="m-0 flex min-w-0 list-none items-center gap-2 p-0" :class="{ 'w-full': props.stretch }">
      <li
        v-if="!props.stretch"
        class="h-0.5 w-7 shrink-0"
        :class="currentVariantLineClass(-1)"
        aria-hidden="true"
      />
      <template v-for="(label, index) in labels" :key="index">
        <li class="flex min-w-0 items-center">
          <VTooltip
            class="flex min-w-0"
            :content="label"
            :disabled="!needsTooltip(index, label)"
            placement="top"
          >
            <template #reference>
              <component
                :is="props.interactive ? 'button' : 'span'"
                :type="props.interactive ? 'button' : undefined"
                class="group relative flex min-w-0 items-center gap-2 rounded-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-800"
                :class="canGoTo(index) ? 'cursor-pointer active:scale-[0.96] transition-transform' : 'cursor-default'"
                :tabindex="!props.interactive && needsTooltip(index, label) ? 0 : undefined"
                :aria-disabled="props.interactive && !isCompleted(index) ? true : undefined"
                :aria-current="isCurrent(index) ? 'step' : undefined"
                :aria-label="label || undefined"
                @click="goToStep(index)"
              >
                <span
                  class="relative grid size-6 shrink-0 place-items-center rounded-full transition-colors after:absolute after:-inset-2 after:rounded-full"
                  :class="badgeClass(index)"
                >
                  <VIcon
                    v-if="isCompleted(index)"
                    name="check"
                    class="block text-[11px]"
                    aria-hidden="true"
                  />
                  <span
                    v-else
                    class="step-number block text-xs leading-none tabular-nums"
                    :class="{ 'font-bold': isCurrent(index) }"
                  >
                    {{ index + 1 }}
                  </span>
                </span>
                <span
                  v-if="showsLabel(index, label)"
                  :data-step-label="index"
                  class="truncate text-sm transition-colors"
                  :class="labelClass(index)"
                >
                  {{ label }}
                </span>
              </component>
            </template>
          </VTooltip>
        </li>
        <li
          v-if="index < labels.length - 1 || !props.stretch"
          class="h-0.5 shrink-0"
          :class="[currentVariantLineClass(index), props.stretch ? 'min-w-4 flex-1' : 'w-7']"
          aria-hidden="true"
        />
      </template>
    </ol>
  </div>
  <div
    v-else-if="labels.length"
    ref="root"
    class="flex items-center w-full min-w-0"
    role="progressbar"
    :aria-valuemin="1"
    :aria-valuemax="labels.length"
    :aria-valuenow="valueNow"
    :aria-valuetext="resolvedValueText"
  >
    <span
      class="flex-1 h-px min-w-4 bg-gray-200 dark:bg-dark-600"
      aria-hidden="true"
    />

    <ol class="flex items-center gap-3 px-3 min-w-0">
      <li
        v-for="(label, index) in labels"
        :key="index"
        class="flex items-center gap-3 min-w-0"
      >
        <VTooltip
          class="flex min-w-0"
          :content="label"
          :disabled="!needsTooltip(index, label)"
          placement="top"
        >
          <template #reference>
            <component
              :is="props.interactive ? 'button' : 'span'"
              :type="props.interactive ? 'button' : undefined"
              class="group relative flex items-center gap-2 min-w-0 rounded-full focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-800"
              :class="canGoTo(index) ? 'cursor-pointer active:scale-[0.96] transition-transform' : 'cursor-default'"
              :tabindex="!props.interactive && needsTooltip(index, label) ? 0 : undefined"
              :aria-disabled="props.interactive && !isCompleted(index) ? true : undefined"
              :aria-current="isCurrent(index) ? 'step' : undefined"
              :aria-label="label || undefined"
              @click="goToStep(index)"
            >
              <span
                class="relative w-5 h-5 rounded-full grid place-items-center shrink-0 transition-colors after:absolute after:-inset-2.5 after:rounded-full"
                :class="badgeClass(index)"
              >
                <VIcon
                  v-if="isCompleted(index)"
                  name="check"
                  class="text-[9px] block"
                  aria-hidden="true"
                />
                <span
                  v-else
                  class="step-number block text-[11px] font-semibold leading-none tabular-nums"
                >
                  {{ index + 1 }}
                </span>
              </span>
              <span
                v-if="showsLabel(index, label)"
                :data-step-label="index"
                class="text-sm font-normal truncate transition-colors"
                :class="labelClass(index)"
              >
                {{ label }}
              </span>
            </component>
          </template>
        </VTooltip>
        <span
          v-if="index < labels.length - 1"
          class="h-px w-8 shrink-0 transition-colors"
          :class="isCompleted(index)
            ? 'bg-primary-300 dark:bg-primary-700'
            : 'bg-gray-200 dark:bg-dark-600'"
          aria-hidden="true"
        />
      </li>
    </ol>

    <span
      class="flex-1 h-px min-w-4 bg-gray-200 dark:bg-dark-600"
      aria-hidden="true"
    />
  </div>
</template>

<style scoped>
/**
 * Centring the line box is not centring the digit: our fallback sans (Roboto is declared
 * app-wide but never bundled) has more ascent than descent, so the glyph lands ~1px high
 * in the 20px badge regardless of line-height. Trimming the box to cap height makes the
 * badge centre the glyph itself, whichever font resolves.
 */
.step-number {
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
}

/* Pre-2025 engines: half the fallback font's error, since the resolved font is not knowable here. */
@supports not (text-box-trim: trim-both) {
  .step-number {
    transform: translateY(0.5px);
  }
}
</style>
